import {
  Badge,
  BoxV2 as Box,
  FilterType,
  Grid,
  InsightCard,
  Table,
  TableFilterV2 as TableFilter,
  useFilters,
  useTable,
} from 'portal-commons';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  faBullhorn,
  faCircleCheck,
  faCommentPen,
} from '@fortawesome/pro-regular-svg-icons';

import { getBadgeTypeForMnoStatus } from '../../utils';
import { campaignController, CampaignListItem, UsecaseType } from '@/apis/core';
import { MnoStatus } from '@/constants/enum';
import { ROUTES } from '@/constants/paths';
import PageContainer from '@/components/PageContainer';
import PageTitle from '@/components/PageTitle';
import { Loader } from '@/shared_elements';
import { selectSmsUseCases } from '@/shared_elements/selectors';
import { humanizeMnoStatus } from '@/utils/string';

const TABLE_HEAD_ROWS = [
  { id: 'uid', label: 'PROGRAM ID', sortable: true },
  { id: 'brandName', label: 'BRAND NAME', sortable: false },
  { id: 'senderId', label: 'SENDER ID', sortable: false },
  { id: 'usecase', label: 'USE CASE', sortable: true },
  { id: 'mnoStatus', label: 'STATUS', sortable: false },
];

const MnoStatusOptions = [
  MnoStatus.Review,
  MnoStatus.Rejected,
  MnoStatus.Registered,
  MnoStatus.Suspended,
].map((mnoStatus) => ({
  label: humanizeMnoStatus(mnoStatus),
  value: mnoStatus,
}));

const FilterConfigs = {
  mnoStatus: {
    type: FilterType.Dropdown,
    options: MnoStatusOptions,
    label: 'Status',
  },
};

const MAX_DISPLAY_RECORDS = 10000;

const DefaultMnoStatus = `${MnoStatus.Review},${MnoStatus.Rejected},${MnoStatus.Registered},${MnoStatus.Suspended}`;

const CampaignListingHU: React.FC = () => {
  const {
    configs: filterConfigs,
    appliedFilters,
    candidateFilters,
    additionalFilters,
    handleEdit,
    handleApply,
  } = useFilters({
    configs: FilterConfigs,
  });
  const useCases = useSelector(selectSmsUseCases);
  const history = useHistory();
  const [statistics, setStatistics] = useState<{
    activeCampaigns: number;
    pendingCampaigns: number;
    registeredCampaigns: number;
  }>({
    activeCampaigns: 0,
    pendingCampaigns: 0,
    registeredCampaigns: 0,
  });
  const useCaseMapping = useMemo(
    () =>
      useCases.reduce((accumulator, useCase) => {
        accumulator[useCase.id] = useCase;
        return accumulator;
      }, {} as Record<string, UsecaseType>),
    [useCases]
  );

  const fetchStatistics = async () => {
    const baseQuery = {
      channelId: 'SMS',
      status: 'ACTIVE',
    };
    const [
      activeCampaignsResult,
      pendingCampaignsResult,
      registeredCampaignsResult,
    ] = await Promise.all([
      campaignController.getCampaigns({
        ...baseQuery,
        mnoStatus: DefaultMnoStatus,
      }),
      campaignController.getCampaigns({
        ...baseQuery,
        mnoStatus: 'REVIEW',
      }),
      campaignController.getCampaigns({
        ...baseQuery,
        mnoStatus: 'REGISTERED',
      }),
    ]);

    setStatistics((prev) => ({
      ...prev,
      activeCampaigns: activeCampaignsResult?.totalRecords || 0,
      pendingCampaigns: pendingCampaignsResult?.totalRecords || 0,
      registeredCampaigns: registeredCampaignsResult?.totalRecords || 0,
    }));
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  const table = useTable<CampaignListItem>(
    async () => {
      const queries: Record<string, unknown> = {
        mnoStatus: DefaultMnoStatus,
        ...table.filter,
        ...appliedFilters,
        ...additionalFilters,
        channelId: 'SMS',
        status: 'ACTIVE',
      };
      const response = await campaignController.getCampaigns({
        ...queries,
      });
      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        rowsPerPage: response?.recordsPerPage || 10,
        page: response?.page || 1,
      };
    },
    {
      cappingTotal: MAX_DISPLAY_RECORDS,
      deps: [appliedFilters, additionalFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const tableData = useMemo(() => {
    const fallbackTo = (
      value: React.ReactNode,
      testId: string,
      fallbackValue = 'N/A'
    ) =>
      typeof value === 'string' ||
      (typeof value === 'number' && !Number.isNaN(value)) ? (
        <Box
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            maxWidth: '350px',
          }}
          data-testid={testId}
        >
          {value}
        </Box>
      ) : (
        value || <span data-testid={testId}>{fallbackValue}</span>
      );

    const renderStatus = (
      status: string | null | undefined,
      testId: string
    ) => {
      if (!status) return <span data-testid={testId}>N/A</span>;
      return (
        <Badge
          size="md"
          shape="rectangle"
          variant="fill"
          type={getBadgeTypeForMnoStatus(status)}
          data-testid={testId}
        >
          {humanizeMnoStatus(status)}
        </Badge>
      );
    };

    return table.tableData.map((data) => ({
      rawUid: data.uid,
      uid: <span data-testid="uid">{data.uid}</span>,
      brandName: fallbackTo(data.brandName, 'brandName'),
      senderId: fallbackTo(data.senderIds?.[0]?.senderIdValue, 'senderId'),
      usecase: fallbackTo(
        useCaseMapping[data.usecase]?.displayName ?? data.usecase,
        'usecase'
      ),
      mnoStatus: renderStatus(data.mnoStatus, 'mnoStatus'),
    }));
  }, [table.tableData]);

  if (table.pageLoading) return <Loader />;

  return (
    <PageContainer sx={{ paddingX: '96px', rowGap: null }}>
      <PageTitle>Messaging Program</PageTitle>
      <Grid
        sx={{
          width: '100%',
          gap: '24px',
          gridTemplateColumns: 'repeat(4, 1fr)',
          marginTop: '26px',
          marginBottom: '56px',
        }}
      >
        <InsightCard
          title="TOTAL PROGRAMS"
          value={statistics.activeCampaigns}
          icon={faBullhorn}
          data-testid="activeCampaigns"
        />
        <InsightCard
          title="PENDING REVIEW"
          value={statistics.pendingCampaigns}
          icon={faCommentPen}
          data-testid="pendingCampaigns"
        />
        <InsightCard
          title="ACTIVE"
          value={statistics.registeredCampaigns}
          icon={faCircleCheck}
          data-testid="registeredCampaigns"
        />
      </Grid>

      <Box sx={{ mb: 'm', width: '100%' }}>
        <TableFilter
          configs={filterConfigs}
          candidateValues={candidateFilters}
          appliedValues={appliedFilters}
          onCandidateValuesChange={handleEdit}
          onAppliedValuesChange={handleApply}
          data-testid="tableFilters"
        />
      </Box>

      <Table
        loading={table.loading}
        headRows={TABLE_HEAD_ROWS}
        emptyState="no programs to view"
        data={tableData}
        rowKey="rawUid"
        handleChangePage={table.handleChangePage}
        createSortHandler={table.handleSorting}
        filter={table.filter}
        pagination={table.pagination}
        emptyBlockHeight="180px"
        data-testid="campaignsTable"
        onRowClick={(data) => {
          history.push(
            ROUTES.PROGRAM_DETAILS.replace(':id', data.rawUid as string)
          );
        }}
      />
    </PageContainer>
  );
};

export default CampaignListingHU;
